import { useCallback, useState } from "react";

export type ActiveDateRangePicker = "from" | "to" | null;

/** Coordinates From/To DatePicker open state so one click switches calendars. */
export function useDateRangePickerOpenState() {
  const [activePicker, setActivePicker] = useState<ActiveDateRangePicker>(null);

  const openPicker = useCallback((picker: Exclude<ActiveDateRangePicker, null>) => {
    setActivePicker(picker);
  }, []);

  const closePicker = useCallback((picker: Exclude<ActiveDateRangePicker, null>) => {
    setActivePicker((current) => (current === picker ? null : current));
  }, []);

  const resetPicker = useCallback(() => {
    setActivePicker(null);
  }, []);

  const getPickerOpenProps = useCallback(
    (picker: Exclude<ActiveDateRangePicker, null>) => ({
      open: activePicker === picker,
      onOpen: () => openPicker(picker),
      onClose: () => closePicker(picker),
    }),
    [activePicker, closePicker, openPicker],
  );

  const getTextFieldOpenOnClick = useCallback(
    (picker: Exclude<ActiveDateRangePicker, null>) => ({
      onClick: () => openPicker(picker),
    }),
    [openPicker],
  );

  const getOpenPickerButtonOnClick = useCallback(
    (picker: Exclude<ActiveDateRangePicker, null>) => ({
      onClick: () => openPicker(picker),
    }),
    [openPicker],
  );

  return {
    activePicker,
    openPicker,
    closePicker,
    resetPicker,
    getPickerOpenProps,
    getTextFieldOpenOnClick,
    getOpenPickerButtonOnClick,
  };
}
